import { Injectable, inject } from '@angular/core';
import { downloadBlob } from '../../../utils/blob-viewer.util';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';

import { Router } from '@angular/router';
import { MipsQuestionnaireActions } from '@store/actions/mips-questionnaire/mips-questionnaire.actions';
import { MipsQuestionnaireService } from '@services/mips-questionnaire.service';

@Injectable()
export class MipsQuestionnaireEffects {
  private readonly actions$ = inject(Actions);
  private readonly mipsService = inject(MipsQuestionnaireService);
  private readonly router = inject(Router);

  loadQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsQuestionnaireActions.loadMIPSQuestionnaire),
      switchMap(({ orgId, performanceYear }) =>
        this.mipsService.getMipsQuestionnaire(orgId, performanceYear).pipe(
          map((data) =>
            MipsQuestionnaireActions.loadMIPSQuestionnaireSuccess({ data })
          ),
          catchError((error) =>
            of(
              MipsQuestionnaireActions.loadMIPSQuestionnaireFailure({
                error: error.message || 'Failed to load questionnaire',
              })
            )
          )
        )
      )
    )
  );

  submitQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsQuestionnaireActions.submitMIPSQuestionnaire),
      switchMap(({ mipsQuestionnaire, orgId, performanceYear }) =>
        this.mipsService.submitMipsQuestionnaire(mipsQuestionnaire, orgId, performanceYear).pipe(
          map((response) =>
            MipsQuestionnaireActions.submitMIPSQuestionnaireSuccess({ response })
          ),
          catchError((error) =>
            of(
              MipsQuestionnaireActions.submitMIPSQuestionnaireFailure({
                error: error.message || 'Failed to submit questionnaire',
              })
            )
          )
        )
      )
    )
  );

  updateQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsQuestionnaireActions.updateMIPSQuestionnaire),
      switchMap(({ mipsQuestionnaire, orgId, performanceYear }) =>
        this.mipsService.updateMipsQuestionnaire(mipsQuestionnaire, orgId, performanceYear).pipe(
          map((response) =>
            MipsQuestionnaireActions.updateMIPSQuestionnaireSuccess({ response })
          ),
          catchError((error) =>
            of(
              MipsQuestionnaireActions.updateMIPSQuestionnaireFailure({
                error: error.message || 'Failed to update questionnaire',
              })
            )
          )
        )
      )
    )
  );

  exportAllQuestionnaires$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsQuestionnaireActions.exportAllQuestionnaires),
      switchMap(({ orgId, performanceYear }) =>
        this.mipsService.exportAllQuestionnaires(orgId, performanceYear).pipe(
          map((response) => {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `mips-questionnaires-export-${performanceYear}-${timestamp}.csv`;
            
            return MipsQuestionnaireActions.exportAllQuestionnairesSuccess({ 
              blob: response, 
              filename 
            });
          }),
          catchError((error) =>
            of(
              MipsQuestionnaireActions.exportAllQuestionnairesFailure({
                error: error.message || 'Failed to export questionnaires',
              })
            )
          )
        )
      )
    )
  );

  exportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsQuestionnaireActions.exportAllQuestionnairesSuccess),
        tap(({ blob, filename }) => downloadBlob(blob, filename))
      ),
    { dispatch: false }
  );

  submitSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsQuestionnaireActions.submitMIPSQuestionnaireSuccess),
        tap(() => {
        })
      ),
    { dispatch: false }
  );

  handleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MipsQuestionnaireActions.submitMIPSQuestionnaireFailure,
          MipsQuestionnaireActions.loadMIPSQuestionnaireFailure,
          MipsQuestionnaireActions.updateMIPSQuestionnaireFailure,
          MipsQuestionnaireActions.exportAllQuestionnairesFailure
        ),
        tap(({ error }) => {
          console.error('MIPS Questionnaire Error:', error);
        })
      ),
    { dispatch: false }
  );
}